/**
 * FathomVoyage — Hub Port Comparison
 * SVG bar chart: port prices by region vs global/regional averages.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

// ── Design tokens ──────────────────────────────────────────────────────────────
const AMBER    = '#e9a020';
const BAR_FILL = '#BA7517';
const SURFACE  = '#0b0f1c';
const BORDER   = '#1e2d44';
const TEXT     = '#b8cae0';
const TEXT2    = '#8892a5';
const TEXT3    = '#3a4558';
const GREEN    = '#2daa6e';

// ── SVG layout ─────────────────────────────────────────────────────────────────
const SVG_W       = 700;
const SVG_H       = 500;
const X_LABEL     = 158;   // right edge of port name text
const X_BAR_START = 170;   // left edge of bars
const X_BAR_END   = 590;   // right edge of bar area
const BAR_AREA_W  = X_BAR_END - X_BAR_START; // 420
const BAR_H       = 14;
const HEADER_H    = 36;
const GLOBAL_Y1   = 50;
const GLOBAL_Y2   = 470;
const LEGEND_Y    = 484;

// ── Cluster definitions ────────────────────────────────────────────────────────
const CLUSTERS = [
  { key: 'emea',     label: 'EMEA',     avgKey: 'emea',     yStart: 50,  yEnd: 174, ports: ['Rotterdam', 'Fujairah'] },
  { key: 'americas', label: 'AMERICAS', avgKey: 'americas', yStart: 174, yEnd: 352, ports: ['Houston', 'LA / Long Beach', 'New York', 'Santos'] },
  { key: 'apac',     label: 'APAC',     avgKey: 'apac',     yStart: 352, yEnd: 470, ports: ['Singapore', 'Hong Kong'] },
];

const ALL_PORTS = CLUSTERS.flatMap(c => c.ports);

const FUELS       = ['vlsfo', 'mgo', 'ifo380'];
const FUEL_LABELS = { vlsfo: 'VLSFO', mgo: 'MGO', ifo380: 'IFO 380' };

// ── Bar Y positions (computed once) ───────────────────────────────────────────
const BAR_Y = (() => {
  const pos = {};
  for (const c of CLUSTERS) {
    const contentStart = c.yStart + HEADER_H;
    const contentH     = c.yEnd - contentStart;
    const spacing      = contentH / c.ports.length;
    c.ports.forEach((p, i) => { pos[p] = contentStart + spacing * (i + 0.5); });
  }
  return pos;
})();

// ── Helpers ───────────────────────────────────────────────────────────────────

// Round the chart floor down to the nearest $50, at 75% of the minimum visible price.
// Ensures bars never reach zero and spreads between prices are visually clear.
function computeFloor(values) {
  const valid = values.filter(v => v != null && v > 0);
  if (valid.length === 0) return 0;
  const min = Math.min(...valid);
  return Math.floor((min * 0.75) / 50) * 50;
}

const easeOut   = t => 1 - Math.pow(1 - t, 3);
const readLS    = (k, d) => { try { return localStorage.getItem(k) ?? d; } catch { return d; } };
const writeLS   = (k, v) => { try { localStorage.setItem(k, v); } catch {} };
const shortName = n => n === 'LA / Long Beach' ? 'LA/LB' : n;

function makeZeroAnim() {
  return {
    widths:   Object.fromEntries(ALL_PORTS.map(p => [p, 0])),
    globalX:  X_BAR_START,
    regionXs: Object.fromEntries(CLUSTERS.map(c => [c.key, X_BAR_START])),
  };
}

// ── Inject CSS (once) ─────────────────────────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('fv-bc-css')) {
  const s = document.createElement('style');
  s.id = 'fv-bc-css';
  s.textContent = `
    .bc-fuel-btn { transition: border-color 0.15s, color 0.15s, background 0.15s; }
    .bc-fuel-btn:hover { opacity: 0.85; }
    .bc-lift-input::-webkit-inner-spin-button,
    .bc-lift-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
    .bc-lift-input[type=number] { -moz-appearance: textfield; }
    @media (max-width: 720px) {
      .bc-header-inner { flex-direction: column !important; align-items: flex-start !important; }
    }
  `;
  document.head.appendChild(s);
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function BunkerComparison({ ports, averages, activeFuel, onFuelChange }) {
  const [fuel,   setFuelRaw] = useState(() => readLS('fv_bunker_fuel', activeFuel || 'vlsfo').toLowerCase());
  const [liftMt, setLiftMt ] = useState(() => parseInt(readLS('fv_bunker_lift_size', '2000')) || 2000);
  const [anim,   setAnim   ] = useState(makeZeroAnim);

  const rafRef       = useRef(null);
  const animRef      = useRef(makeZeroAnim());
  const hasAnimated  = useRef(false);
  const targetsRef   = useRef(null);
  const containerRef = useRef(null);

  const setFuel = useCallback((f) => {
    setFuelRaw(f);
    writeLS('fv_bunker_fuel', f);
    onFuelChange?.(f);
  }, [onFuelChange]);

  // Sync fuel with parent when it changes externally
  useEffect(() => {
    if (activeFuel && activeFuel !== fuel) setFuelRaw(activeFuel);
  }, [activeFuel]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Port lookup ──────────────────────────────────────────────────────────
  // ports can be: Worker array [{port, vlsfo, ...}] or tablePorts object {name: {VLSFO:{price},...}}
  const portLookup = useMemo(() => {
    const lk = {};
    if (!ports) return lk;
    const entries = Array.isArray(ports) ? ports : Object.values(ports);
    for (const p of entries) {
      const name = p.port || p.name;
      if (name) lk[name] = p;
    }
    return lk;
  }, [ports]);

  // ── Derive targets ────────────────────────────────────────────────────────
  const targets = useMemo(() => {
    const fkLc = fuel; // already lowercase ('vlsfo' | 'mgo' | 'ifo380')

    const portPrices = {};
    for (const portName of ALL_PORTS) {
      const p = portLookup[portName];
      if (!p) { portPrices[portName] = null; continue; }
      // Worker flat shape: p.vlsfo  |  tablePorts nested shape: p.VLSFO?.price
      portPrices[portName] = p[fkLc] ?? p[fkLc.toUpperCase()]?.price ?? null;
    }

    const fkAvg = fkLc; // averages always use lowercase keys from worker
    const globalAvg  = averages?.global_20?.[fkAvg] ?? null;
    const regionAvgs = {};
    for (const c of CLUSTERS) {
      regionAvgs[c.key] = averages?.[c.avgKey]?.[fkAvg] ?? null;
    }

    const allPrices = [
      ...Object.values(portPrices),
      globalAvg,
      ...Object.values(regionAvgs),
    ].filter(v => v != null);

    const floor      = computeFloor(allPrices);
    const maxPrice   = allPrices.length ? Math.max(...allPrices) : 1000;
    const headroom   = (maxPrice - floor) * 0.05;
    const scaleFactor = BAR_AREA_W / (maxPrice - floor + headroom || 1);
    const scale = (price) => price == null ? 0 : Math.max(4, (price - floor) * scaleFactor);

    return { portPrices, globalAvg, regionAvgs, maxPrice, floor, scale };
  }, [portLookup, averages, fuel]);

  // Keep targetsRef current for the IntersectionObserver closure
  useEffect(() => { targetsRef.current = targets; }, [targets]);

  // ── Cheapest hub ──────────────────────────────────────────────────────────
  const cheapestPort = useMemo(() => {
    let best = null, bestP = Infinity;
    for (const [n, p] of Object.entries(targets.portPrices)) {
      if (p != null && p < bestP) { bestP = p; best = n; }
    }
    return best;
  }, [targets]);

  // ── Animation engine ──────────────────────────────────────────────────────
  const runAnimation = useCallback((fromAnim, toTargets, duration, stagger) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const toWidths = Object.fromEntries(
      ALL_PORTS.map(p => [p, toTargets.scale(toTargets.portPrices[p])])
    );
    const toGlobalX  = X_BAR_START + toTargets.scale(toTargets.globalAvg);
    const toRegionXs = Object.fromEntries(
      CLUSTERS.map(c => [c.key, X_BAR_START + toTargets.scale(toTargets.regionAvgs[c.key])])
    );

    const fromWidths   = { ...fromAnim.widths };
    const fromGlobalX  = fromAnim.globalX;
    const fromRegionXs = { ...fromAnim.regionXs };

    const start = performance.now();

    function step(now) {
      const elapsed  = now - start;
      const newWidths = {};
      let anyRunning  = false;

      for (let i = 0; i < ALL_PORTS.length; i++) {
        const p          = ALL_PORTS[i];
        const portElapsed = Math.max(0, elapsed - stagger * i);
        const t          = Math.min(1, portElapsed / duration);
        const e          = easeOut(t);
        newWidths[p]     = (fromWidths[p] || 0) + (toWidths[p] - (fromWidths[p] || 0)) * e;
        if (t < 1) anyRunning = true;
      }

      const refT   = Math.min(1, elapsed / duration);
      const refE   = easeOut(refT);
      const newGX  = fromGlobalX + (toGlobalX - fromGlobalX) * refE;
      const newRXs = Object.fromEntries(
        CLUSTERS.map(c => {
          const fr = fromRegionXs[c.key] || X_BAR_START;
          return [c.key, fr + (toRegionXs[c.key] - fr) * refE];
        })
      );
      if (refT < 1) anyRunning = true;

      const next = { widths: newWidths, globalX: newGX, regionXs: newRXs };
      animRef.current = next;
      setAnim(next);

      if (anyRunning) rafRef.current = requestAnimationFrame(step);
    }

    rafRef.current = requestAnimationFrame(step);
  }, []);

  // IntersectionObserver — fire entrance animation once on first visible frame
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated.current) {
        hasAnimated.current = true;
        runAnimation(makeZeroAnim(), targetsRef.current || targets, 600, 80);
        obs.disconnect();
      }
    }, { threshold: 0.1 });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-animate when fuel/data changes (after entrance has fired)
  const prevTargetsRef = useRef(null);
  useEffect(() => {
    if (!hasAnimated.current) return;
    if (prevTargetsRef.current === targets) return;
    prevTargetsRef.current = targets;
    runAnimation(animRef.current, targets, 400, 0);
  }, [targets, runAnimation]);

  // Cleanup RAF on unmount
  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  // ── Insight values ────────────────────────────────────────────────────────
  const cheapestPrice = cheapestPort ? targets.portPrices[cheapestPort] : null;
  const { globalAvg } = targets;
  const cheapestDiff  = cheapestPrice != null && globalAvg != null ? cheapestPrice - globalAvg : null;
  const totalSavings  = cheapestDiff != null ? Math.abs(cheapestDiff) * liftMt : null;

  return (
    <div
      ref={containerRef}
      style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden', marginBottom: 20 }}
    >
      {/* ── Header ── */}
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${BORDER}` }}>
        <div className="bc-header-inner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 13, fontWeight: 600, color: '#fff' }}>
              Hub Port Comparison
            </div>
            <div style={{ fontSize: 11, color: TEXT2, marginTop: 2 }}>
              Current prices by region vs global &amp; regional averages
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {FUELS.map(f => (
              <button
                key={f}
                className="bc-fuel-btn"
                onClick={() => setFuel(f)}
                style={{
                  padding: '4px 10px', borderRadius: 20,
                  border: `1px solid ${f === fuel ? AMBER : BORDER}`,
                  background: f === fuel ? 'rgba(233,160,32,0.15)' : 'transparent',
                  color: f === fuel ? AMBER : TEXT2,
                  fontSize: 11, fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer',
                }}
              >
                {FUEL_LABELS[f]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── SVG Chart ── */}
      <svg
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block' }}
      >
        <rect x={0} y={0} width={SVG_W} height={SVG_H} fill={SURFACE} />

        {/* Cluster dividers */}
        <line x1={0} y1={174} x2={SVG_W} y2={174} stroke={BORDER} strokeWidth={1} />
        <line x1={0} y1={352} x2={SVG_W} y2={352} stroke={BORDER} strokeWidth={1} />

        {/* Global 20 vertical line */}
        <line
          x1={anim.globalX} y1={GLOBAL_Y1}
          x2={anim.globalX} y2={GLOBAL_Y2}
          stroke={AMBER} strokeWidth={2}
        />
        {/* Global 20 top label */}
        <text
          x={anim.globalX} y={42}
          fill={AMBER} fontSize={10}
          fontFamily="'JetBrains Mono', monospace"
          textAnchor="middle"
        >
          {`GLOBAL 20${globalAvg != null ? ` · $${Math.round(globalAvg)}` : ''}`}
        </text>

        {/* Clusters */}
        {CLUSTERS.map(c => {
          const regionX   = anim.regionXs[c.key] ?? X_BAR_START;
          const regionAvg = targets.regionAvgs[c.key];

          return (
            <g key={c.key}>
              {/* Region header label */}
              <text
                x={10} y={c.yStart + 22}
                fill={TEXT2} fontSize={9}
                fontFamily="'JetBrains Mono', monospace"
                fontWeight="600"
                letterSpacing="1"
              >
                {c.label}
              </text>

              {/* Region avg label next to header */}
              {regionAvg != null && (
                <text
                  x={10} y={c.yStart + 32}
                  fill={TEXT3} fontSize={8}
                  fontFamily="'JetBrains Mono', monospace"
                >
                  {`avg $${Math.round(regionAvg)}`}
                </text>
              )}

              {/* Regional avg dashed line */}
              {regionAvg != null && (
                <line
                  x1={regionX} y1={c.yStart + HEADER_H}
                  x2={regionX} y2={c.yEnd}
                  stroke={BAR_FILL} strokeWidth={1.5} strokeDasharray="4 3"
                />
              )}

              {/* Port bars */}
              {c.ports.map(portName => {
                const y     = BAR_Y[portName];
                const price = targets.portPrices[portName];
                const w     = Math.max(0, anim.widths[portName] ?? 0);

                return (
                  <g key={portName}>
                    {/* Port name */}
                    <text
                      x={X_LABEL} y={y + 4}
                      fill={TEXT} fontSize={10}
                      fontFamily="'JetBrains Mono', monospace"
                      textAnchor="end"
                    >
                      {shortName(portName)}
                    </text>

                    {/* Bar — only render when width > 0; clamp to 4px minimum at rest */}
                    {w > 0 && (
                      <rect
                        x={X_BAR_START} y={y - BAR_H / 2}
                        width={Math.max(4, w)} height={BAR_H}
                        rx={2} fill={BAR_FILL} fillOpacity={0.85}
                      />
                    )}

                    {/* Price label to the right of bar area */}
                    <text
                      x={X_BAR_END + 8} y={y + 4}
                      fill={price != null ? TEXT : TEXT3}
                      fontSize={10}
                      fontFamily="'JetBrains Mono', monospace"
                    >
                      {price != null ? `$${Math.round(price)}` : '—'}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Floor indicator — updates on fuel toggle */}
        {targets.floor > 0 && (
          <text
            x={X_BAR_START} y={476}
            fill={TEXT3} fontSize={9}
            fontFamily="'JetBrains Mono', monospace"
            textAnchor="start"
          >
            {`$${targets.floor} ↓`}
          </text>
        )}

        {/* Legend */}
        <g>
          {/* Global 20 */}
          <line x1={X_BAR_START} y1={LEGEND_Y} x2={X_BAR_START + 20} y2={LEGEND_Y} stroke={AMBER} strokeWidth={2} />
          <text x={X_BAR_START + 26} y={LEGEND_Y + 4} fill={TEXT2} fontSize={9} fontFamily="'JetBrains Mono', monospace">
            Global 20 avg
          </text>
          {/* Regional avg */}
          <line x1={X_BAR_START + 130} y1={LEGEND_Y} x2={X_BAR_START + 150} y2={LEGEND_Y} stroke={BAR_FILL} strokeWidth={1.5} strokeDasharray="4 3" />
          <text x={X_BAR_START + 156} y={LEGEND_Y + 4} fill={TEXT2} fontSize={9} fontFamily="'JetBrains Mono', monospace">
            Regional avg
          </text>
          {/* Bar swatch */}
          <rect x={X_BAR_START + 260} y={LEGEND_Y - 5} width={20} height={10} rx={2} fill={BAR_FILL} fillOpacity={0.85} />
          <text x={X_BAR_START + 286} y={LEGEND_Y + 4} fill={TEXT2} fontSize={9} fontFamily="'JetBrains Mono', monospace">
            Port price
          </text>
        </g>
      </svg>

      {/* ── Insight line ── */}
      {cheapestPort != null && cheapestDiff != null && (
        <div style={{ padding: '10px 20px 14px', borderTop: `1px solid ${BORDER}` }}>
          <span style={{ fontFamily: "'Fraunces', serif", fontStyle: 'italic', fontSize: 15, color: TEXT, lineHeight: 1.6 }}>
            <span style={{ color: AMBER }}>{shortName(cheapestPort)}</span>
            {' is cheapest at '}
            <span style={{ color: AMBER }}>${Math.round(cheapestPrice ?? 0)}</span>
            {cheapestDiff < 0 && (
              <> — <span style={{ color: GREEN }}>${Math.round(Math.abs(cheapestDiff))}/mt below</span> the global average</>
            )}
            {'. On a '}
            <input
              type="number"
              className="bc-lift-input"
              value={liftMt}
              min={100}
              max={50000}
              step={100}
              onChange={e => {
                const v = Math.max(100, parseInt(e.target.value) || 2000);
                setLiftMt(v);
                writeLS('fv_bunker_lift_size', String(v));
              }}
              style={{
                width: 64, background: 'transparent', border: 'none',
                borderBottom: `1px solid ${BORDER}`,
                color: AMBER, fontFamily: "'Fraunces', serif", fontStyle: 'italic', fontSize: 15,
                textAlign: 'center', outline: 'none', padding: '0 2px',
              }}
            />
            {' mt lift, that\'s '}
            {totalSavings != null && (
              <span style={{ color: GREEN }}>
                ${Math.round(totalSavings).toLocaleString()} saved
              </span>
            )}
            {' vs global average.'}
          </span>
        </div>
      )}
    </div>
  );
}
